import { useEffect, useState } from 'react'
import { Modal } from '../ui/Modal'
import type { Visibility } from './reportsStore'
import type { Locale } from './types'

/**
 * "Salveaza" / "Salveaza o copie" / detaliile unui raport: nume, descriere,
 * cine il vede. Numele e singurul camp cerut.
 */

export interface SaveFields {
  name: string
  description: string
  visibility: Visibility
}

export function SaveDialog({ open, mode, initial, locale, hint = '', error = '', onClose, onSave }: {
  open: boolean
  /** O propozitie deasupra campurilor (de pilda: "salveaza intai, apoi programezi"). */
  hint?: string
  /** Refuzul serverului, tradus (ex. "Rolul tau nu are acest drept"). */
  error?: string
  mode: 'save' | 'copy' | 'details'
  initial: SaveFields
  locale: Locale
  onClose(): void
  onSave(fields: SaveFields): void
}) {
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const [fields, setFields] = useState(initial)
  const [tried, setTried] = useState(false)

  /* La fiecare deschidere, campurile pornesc de la ce da pagina. */
  useEffect(() => {
    if (open) {
      setFields(initial)
      setTried(false)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const missing = fields.name.trim() === ''
  const submit = () => {
    setTried(true)
    if (!missing) onSave({ ...fields, name: fields.name.trim(), description: fields.description.trim() })
  }
  const title = mode === 'copy' ? t('Salveaza o copie', 'Save as copy') : mode === 'details' ? t('Detaliile raportului', 'Report details') : t('Salveaza raportul', 'Save report')

  return (
    <Modal
      id="od-rep-save"
      labelledBy="od-rep-save-title"
      open={open}
      onClose={onClose}
      portal
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t('Renunta', 'Cancel')}
          </button>
          <button type="button" className="btn btn-primary od-rep-save-confirm" onClick={submit}>
            {mode === 'details' ? t('Pastreaza', 'Update') : t('Salveaza', 'Save')}
          </button>
        </>
      }
    >
      <form
        className="od-rep-form"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {hint ? <p className="od-rep-form-hint">{hint}</p> : null}
        <label className="od-rep-form-row">
          <span>{t('Nume', 'Name')}</span>
          <input
            className={`form-control${tried && missing ? ' od-rep-invalid' : ''}`}
            value={fields.name}
            maxLength={120}
            autoFocus
            aria-invalid={tried && missing}
            onChange={(e) => setFields({ ...fields, name: e.target.value })}
          />
          {tried && missing ? <span className="od-rep-form-error">{t('Raportul are nevoie de un nume.', 'The report needs a name.')}</span> : null}
        </label>
        <label className="od-rep-form-row">
          <span>
            {t('Descriere', 'Description')} <em className="od-rep-optional">{t('(optional)', '(optional)')}</em>
          </span>
          <textarea className="form-control" rows={2} maxLength={500} value={fields.description} onChange={(e) => setFields({ ...fields, description: e.target.value })} />
        </label>
        <fieldset className="od-rep-form-row">
          <legend>{t('Cine il vede', 'Who can see it')}</legend>
          <label className="od-rep-radio">
            <input type="radio" name="od-rep-vis" checked={fields.visibility === 'private'} onChange={() => setFields({ ...fields, visibility: 'private' })} />
            <span>
              <b>{t('Doar eu', 'Only me')}</b> <span className="od-rep-meta">{t('raportul e al tau', 'the report is yours')}</span>
            </span>
          </label>
          <label className="od-rep-radio">
            <input type="radio" name="od-rep-vis" checked={fields.visibility === 'company'} onChange={() => setFields({ ...fields, visibility: 'company' })} />
            <span>
              <b>{t('Managerii companiei', 'Company managers')}</b> <span className="od-rep-meta">{t('il pot deschide si ei', 'they can open it too')}</span>
            </span>
          </label>
        </fieldset>
        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
